import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import { formatParasite } from "utils/parasite";
import moment from "moment";
import React, { useState } from "react";
import { useEffect } from "react";
import { CheckSquare } from "react-feather";
import Skl5 from "services/SurveilansKnowlesiLab5";
import {
  getCurrentUserFromLocalStorage,
  setDataToLocalStorage,
} from "utils/common";
import { MonthOptions, SampleSendTypeOptions } from "utils/constant";
import { actionState } from "./sampleTypeRules";

const Skl5Service = new Skl5();

const TableComponent = ({ changeMode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [filterState, setFilterState] = useState({
    selected_year: moment().format("YYYY"),
    selected_month: "",
    selected_sample_type: "",
  });

  const [skl5, setSkl5] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });

  const currentUser = getCurrentUserFromLocalStorage();

  const getSkl5Temp = async () => {
    let searchBy = "destination_lab_id";
    let searchValue = currentUser.username + "|" + currentUser.first_name;
    setIsLoading(true);
    const searchByDate =
      filterState?.selected_year && filterState?.selected_month
        ? "|YYYY-MM"
        : filterState?.selected_year
        ? "|YYYY"
        : filterState?.selected_month
        ? "|MM"
        : "";
    const searchValueDate =
      filterState?.selected_year && filterState?.selected_month
        ? `${filterState?.selected_year}-${filterState?.selected_month}`
        : filterState?.selected_year
        ? `${filterState?.selected_year}`
        : filterState?.selected_month
        ? `${filterState?.selected_month}`
        : "";

    if (filterState.selected_year || filterState.selected_month) {
      searchBy += `,sample_send_date${searchByDate}`;
      searchValue += `,${searchValueDate}`;
    }

    if (filterState.selected_sample_type) {
      searchBy += `,sample_send_type`;
      searchValue += `,${filterState.selected_sample_type}`;
    }

    try {
      const response = await Skl5Service.getAll({
        page: skl5.page,
        limit: skl5.limit,
        searchBy: searchBy,
        searchValue: searchValue,
      });

      setSkl5((prevState) => ({
        ...prevState,
        data: response.surveilans_knowlesi_lab_5,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSkl5Temp();
  }, [skl5.page]);

  const onPageChange = (page) => {
    setSkl5((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => {
        const state = actionState(row.original);
        if (state === "edit-checked") {
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div className="button-actions">
                <ActionButton
                  onClick={() => {
                    setDataToLocalStorage(
                      "SELECTED_ITEM",
                      JSON.stringify(row.original)
                    );
                    changeMode("edit");
                  }}
                  btnType={"edit"}
                />
              </div>
              <div
                style={{
                  color: "#198754",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CheckSquare size={18} className="mr-2" />
                Sudah Diperiksa
              </div>
            </div>
          );
        } else if (state === "edit") {
          return (
            <div className="button-actions">
              <ActionButton
                onClick={() => {
                  setDataToLocalStorage(
                    "SELECTED_ITEM",
                    JSON.stringify(row.original)
                  );
                  changeMode("edit");
                }}
                btnType={"edit"}
              />
            </div>
          );
        } else {
          return (
            <div
              style={{
                color: "#198754",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <CheckSquare size={18} className="mr-2" />
              Sudah Diperiksa
            </div>
          );
        }
      },
    },
    {
      header: "Provinsi",
      accessorKey: "province_name",
    },
    {
      header: "Kab/Kota",
      accessorKey: "district_name",
    },
    {
      header: "Fasyankes Pencatat",
      accessorKey: "fasyankes_name",
    },
    {
      header: "Kode Slide",
      accessorKey: "slide_code",
    },
    {
      header: "Nama pasien",
      accessorKey: "patient_name",
    },
    {
      header: "Alamat Domisili Pasien",
      accessorKey: "address",
    },
    {
      header: "Pekerjaan pasien",
      accessorKey: "job",
    },
    {
      header: "Jenis Parasit",
      accessorKey: "parasyte",
      cell: ({ getValue }) => formatParasite(getValue()),
    },
    {
      header: "Lab Pengirim Sampel",
      accessorKey: "source_lab_id",
      cell: ({ row }) =>
        (row.original.source_lab_id || row.original.source_fasyankes_id)?.split(
          "|"
        )[1],
    },
    {
      header: "Tanggal Sampel dikirim",
      accessorKey: "sample_send_date",
    },
    {
      header: "Tanggal sampel diterima",
      accessorKey: "sample_received_date",
    },
    {
      header:
        "Kondisi Sampel Sediaan Apusan Darah (SAD) (Memenuhi syarat pemeriksaan PCR)",
      accessorKey: "sample_sad_condition",
    },
    {
      header: "Umpan Balik Kondisi Sampel Sediaan Apusan Darah (SAD)",
      accessorKey: "sample_sad_feedback",
    },
    {
      header:
        "Kondisi Sampel Dried Blood Spot (DBS) (Memenuhi syarat pemeriksaan PCR)",
      accessorKey: "sample_dbs_condition",
    },
    {
      header: "Umpan Balik Kondisi Sampel Dried Blood Spot (DBS)",
      accessorKey: "sample_dbs_feedback",
    },
    {
      header:
        "Kondisi Sampel Whole Blood EDTA (WB) (Memenuhi syarat pemeriksaan PCR)",
      accessorKey: "sample_wb_condition",
    },
    {
      header: "Umpan Balik Kondisi Sampel Whole Blood EDTA (WB)",
      accessorKey: "sample_wb_feedback",
    },
    {
      header: "Tanggal selesai pemeriksaan PCR",
      accessorKey: "pcr_finish_checking_date",
    },
    {
      header: "Hasil Pemeriksaan PCR",
      accessorKey: "pcr_checking_result",
      cell: ({ getValue }) => formatParasite(getValue()),
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: skl5.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">
        Input Surveilans {formatParasite("Knowlesi")} - Lab
      </h1>

      {/* Informasi wilayah */}
      <div className="informasi-wilayah">
        <section className="list-item">
          <p className="item-label">Provinsi</p>
          <p className="item-value">
            {currentUser?.province_id?.split("|")[1]}
          </p>
        </section>
        <hr />
        <section className="list-item">
          <p className="item-label">Kab/Kota</p>
          <p className="item-value">
            {currentUser?.district_id?.split("|")[1]}
          </p>
        </section>
        <hr />
        <section className="list-item">
          <p className="item-label">Laboratorium</p>
          <p className="item-value">{currentUser?.first_name}</p>
        </section>
      </div>

      <div className="card-filter">
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih Tahun"}
            name={"selected_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              const selectedYear = moment(e).format("YYYY");
              setFilterState({
                ...filterState,
                selected_year: selectedYear,
              });
            }}
            value={filterState.selected_year}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih Bulan"}
            data={MonthOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState({
                ...filterState,
                selected_month: selectedValue,
              });
            }}
            value={MonthOptions.find(
              (item) => item.value === filterState.selected_month
            )}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih Jenis Sampel"}
            data={SampleSendTypeOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState({
                ...filterState,
                selected_sample_type: selectedValue,
              });
            }}
            value={SampleSendTypeOptions.find(
              (item) => item.value === filterState.selected_sample_type
            )}
          />
        </div>
        <div className="filter-action">
          <Button
            variant={"primary"}
            size={"normal"}
            onClick={() => {
              getSkl5Temp();
            }}
            isDisabled={isLoading}
          >
            Terapkan
          </Button>
        </div>
      </div>

      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={skl5.currentPage}
        limit={skl5.limit}
        nextPage={skl5.nextPage}
        previousPage={skl5.previousPage}
        total={skl5.totalData}
      />
    </div>
  );
};

export default TableComponent;
